import { List, ListRow, Section, Skeleton, Text } from '@ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useColonyYield, useLocationYield, type YieldYear } from '../api';
import { formatKg } from '../format';

function Years({ years }: { years: YieldYear[] }) {
  return (
    <List>
      {years.map((y) => (
        <ListRow
          key={y.year}
          title={String(y.year)}
          description={y.varieties
            .map((v) => `${v.variety.name} ${formatKg(v.grams, 1)}`)
            .join(' · ')}
          trailing={formatKg(y.grams, 1)}
        />
      ))}
    </List>
  );
}

/** Yield of a colony per year and variety (VOL-005). Hidden while there is none. */
export function ColonyYieldCard({ colonyId }: { colonyId: string }) {
  const { t } = useTranslation();
  const data = useColonyYield(colonyId);
  const last = data.data?.shares[0];
  if (data.data && data.data.years.length === 0) return null;
  return (
    <Section
      title={t('harvest.yield.colony')}
      footer={
        last && (
          <>
            {t('harvest.yield.lastBatch')} <Link to={`/ernte/${last.batch_id}`}>{last.lot}</Link>
          </>
        )
      }
    >
      {data.isPending && <Skeleton lines={2} />}
      {data.data && <Years years={data.data.years} />}
    </Section>
  );
}

/** Yield of the batches of a location per year (LOC-004). */
export function LocationYieldCard({ locationId }: { locationId: string }) {
  const { t } = useTranslation();
  const data = useLocationYield(locationId);
  return (
    <Section title={t('harvest.yield.location')}>
      {data.isPending && <Skeleton lines={2} />}
      {data.data && data.data.years.length === 0 && (
        <Text size="sm" tone="muted" style={{ padding: 'var(--space-3) var(--space-4)' }}>
          {t('harvest.yield.noneLocation')}
        </Text>
      )}
      {data.data && <Years years={data.data.years} />}
    </Section>
  );
}
